import { useInviteUserMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { OrganizationRole, SendInviteRequest } from '@consigli/types';
import { useCallback, useState } from 'react';

import { SheetForm } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { InviteFormFields } from './invite-form-fields';
import { defaultInviteFormData, FormErrors, InviteFormData } from './types';
import { validateInviteForm } from './validation';

export const InviteUserForm = () => {
  const [formData, setFormData] = useState<InviteFormData>(defaultInviteFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isOpen, setIsOpen] = useState(false);

  const [inviteUser, { isLoading }] = useInviteUserMutation();

  const handleFormChange = useCallback((data: Partial<InviteFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    // Clear errors for changed fields
    setErrors((prevErrors) => {
      const clearedErrors = { ...prevErrors };
      Object.keys(data).forEach((key) => {
        delete clearedErrors[key as keyof FormErrors];
      });
      return clearedErrors;
    });
  }, []);

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const validation = validateInviteForm(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }

    const dedupedRecipients = Array.from(
      new Set(formData.recipients.map((r) => r.toLowerCase().trim()).filter(Boolean)),
    );

    try {
      const request: SendInviteRequest = {
        recipients: dedupedRecipients,
        language: formData.language!,
        organization:
          formData.organizationRole === OrganizationRole.ADMIN ||
          (formData.organizationRole === OrganizationRole.USER && formData.canCreateProjects)
            ? {
                id: formData.organizationId!,
                role:
                  formData.organizationRole === OrganizationRole.ADMIN
                    ? OrganizationRole.ADMIN
                    : OrganizationRole.USER,
              }
            : undefined,
        projects:
          formData.organizationRole === OrganizationRole.USER
            ? formData.projects.map((p) => ({
                id: p.id,
                role: p.role,
                permissions: p.permissions,
              }))
            : undefined,
      };

      const created = await inviteUser(request).unwrap();
      const count = created?.length ?? dedupedRecipients.length;
      successToast({
        message: count === 1 ? 'Invite sent successfully!' : `Sent ${count} invites successfully!`,
      });

      setFormData(defaultInviteFormData);
      setErrors({});
      setIsOpen(false);
      return true;
    } catch (error) {
      console.error('Failed to send invite:', error);
      const status = (error as { status?: number })?.status;
      if (status === 400 || status === 403) {
        errorToast({
          message: 'Invites were rejected. No invitations were created.',
        });
      } else {
        errorToast({ message: 'Failed to send invite. Please try again.' });
      }
      return false;
    }
  }, [formData, inviteUser]);

  const handleCancel = useCallback(() => {
    setFormData(defaultInviteFormData);
    setErrors({});
  }, []);

  return (
    <SheetForm
      title="Invite User"
      description="Send an invitation to a new user to join the platform."
      trigger={<Button>Invite User</Button>}
      submitLabel="Send Invite"
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSubmitting={isLoading}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <InviteFormFields formData={formData} errors={errors} onChange={handleFormChange} />
    </SheetForm>
  );
};
